import React from 'react';
import { Video } from 'lucide-react';
import { HoverTip } from '../billing/SupplierBillHover';
import type { ProjectMeeting } from '../../types';
import {
  formatMeetingWhen,
  meetingSummaryDisplay,
} from '../../utils/projectMeetingsTimeline';
import './GanttMeetingDayCell.css';

export function MeetingTipContent({ meetings }: { meetings: ProjectMeeting[] }) {
  return (
    <div className="pm-gantt-meeting-tip">
      {meetings.map((m) => {
        const summaryText = meetingSummaryDisplay(m.summary);
        const attendees =
          m.attendees.length > 0 ? m.attendees.join(', ') : 'No attendees listed';
        return (
          <div key={m.id || m.calendarEventId} className="pm-gantt-meeting-tip-block">
            <div className="pm-gantt-meeting-tip-title">{m.title || 'Meeting'}</div>
            <div className="pm-gantt-meeting-tip-when">{formatMeetingWhen(m.start, m.end)}</div>
            <div className="pm-gantt-meeting-tip-attendees">{attendees}</div>
            <div className="pm-gantt-meeting-tip-summary">{summaryText}</div>
            {m.recordingUrl ? (
              <a
                className="pm-gantt-meeting-tip-link"
                href={m.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Open recording
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Small meeting marker in a Gantt day header cell. */
export function GanttMeetingDayCell({
  meetings,
  compact,
}: {
  meetings: ProjectMeeting[];
  compact?: boolean;
}) {
  if (!meetings.length) return null;
  const label =
    meetings.length === 1
      ? meetings[0]!.title || 'Meeting'
      : `${meetings.length} meetings`;

  return (
    <HoverTip tip={<MeetingTipContent meetings={meetings} />} wide>
      <span className="pm-gantt-day-meeting" aria-label={label}>
        <Video size={11} strokeWidth={2.25} className="pm-gantt-day-meeting-icon" aria-hidden />
        {meetings.length > 1 && !compact && (
          <span className="pm-gantt-day-meeting-count">+{meetings.length - 1}</span>
        )}
      </span>
    </HoverTip>
  );
}

/** Superscript on a payment flag when that day also has meetings. */
export function GanttMeetingFlagBadge({ meetings }: { meetings: ProjectMeeting[] }) {
  if (!meetings.length) return null;
  const label = meetings.length === 1 ? '1 meeting' : `${meetings.length} meetings`;
  return (
    <span className="pm-gantt-day-meeting-plus" aria-label={label}>
      +{meetings.length}
    </span>
  );
}
